import { Link } from 'react-router-dom';
import { Menu, Bell, User, LogOut, Clock, Sun, Moon, Coins, ChevronRight } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { useWorkoutStore } from '@/store/workoutStore';
import { userApi, notificationApi } from '@/services/api';
import { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from '@/components/common';
import type { SystemNotification } from '@/types';

export const Header = () => {
  const { user, isAuthenticated, logout, setUser } = useAuthStore();
  const { toggleSidebar, darkMode, setDarkMode } = useUIStore();
  const { activeWorkout, loadActiveWorkout } = useWorkoutStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<SystemNotification[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<SystemNotification | null>(null);
  const [markingRead, setMarkingRead] = useState(false);
  const [workoutTime, setWorkoutTime] = useState('00:00:00');

  // Initialize dark mode from user preference
  useEffect(() => {
    if (user?.darkMode !== undefined) {
      setDarkMode(user.darkMode);
    }
  }, [user?.darkMode, setDarkMode]);

  // Handle dark mode toggle and save to profile
  const handleToggleDarkMode = async () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    
    if (user) {
      try {
        const response = await userApi.update(user.id, { darkMode: newDarkMode });
        if (response.success && response.data) {
          setUser(response.data.user);
        }
      } catch (error) {
        console.error('Failed to save dark mode preference:', error);
      }
    }
  };

  // Load active workout on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadActiveWorkout();
    }
  }, [isAuthenticated, loadActiveWorkout]);

  const loadNotifications = useCallback(async () => {
    try {
      const response = await notificationApi.getUnread();
      if (response.success && response.data) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      return;
    }

    const NOTIFICATION_POLL_MS = 5 * 60 * 1000;

    loadNotifications();

    const interval = setInterval(loadNotifications, NOTIFICATION_POLL_MS);

    const refreshOnVisible = () => {
      if (document.visibilityState === 'visible') {
        loadNotifications();
      }
    };

    const refreshOnPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        loadNotifications();
      }
    };

    document.addEventListener('visibilitychange', refreshOnVisible);
    window.addEventListener('focus', loadNotifications);
    window.addEventListener('pageshow', refreshOnPageShow);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshOnVisible);
      window.removeEventListener('focus', loadNotifications);
      window.removeEventListener('pageshow', refreshOnPageShow);
    };
  }, [isAuthenticated, loadNotifications]);

  useEffect(() => {
    if (showNotifications && isAuthenticated) {
      loadNotifications();
    }
  }, [showNotifications, isAuthenticated, loadNotifications]);

  const openNotification = (notification: SystemNotification) => {
    setSelectedNotification(notification);
    setShowNotifications(false);
  };

  const closeNotificationModal = () => {
    setSelectedNotification(null);
  };

  const handleMarkAsRead = async () => {
    if (!selectedNotification) return;

    setMarkingRead(true);
    try {
      const response = await notificationApi.markAsRead(selectedNotification.id);
      if (response.success) {
        setNotifications((prev) => prev.filter((n) => n.id !== selectedNotification.id));
        setSelectedNotification(null);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    } finally {
      setMarkingRead(false);
    }
  };

  const formatNotificationDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  // Format elapsed time
  const formatElapsedTime = useCallback((startTime: string) => {
    const start = new Date(startTime).getTime();
    const now = Date.now();
    const diff = Math.floor((now - start) / 1000);
    
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Live timer for active workout
  useEffect(() => {
    if (!activeWorkout) return;
    
    setWorkoutTime(formatElapsedTime(activeWorkout.startTime));
    
    const interval = setInterval(() => {
      setWorkoutTime(formatElapsedTime(activeWorkout.startTime));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [activeWorkout, formatElapsedTime]);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Mobile menu button */}
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Active workout indicator (center) */}
          <div className="flex-1 flex justify-center">
            {activeWorkout && isAuthenticated && (
              <Link
                to="/workout"
                className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-full transition-colors"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                </span>
                <Clock className="w-4 h-4 text-primary-600" />
                <span className="font-mono text-sm font-semibold text-primary-700">
                  {workoutTime}
                </span>
                <span className="hidden sm:inline text-xs text-primary-600">
                  Aktív edzés
                </span>
              </Link>
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-1">
            {isAuthenticated ? (
              <>
                {/* Credits display */}
                <Link
                  to="/profile"
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                  title="Kredit egyenleg – kattints a részletekért"
                >
                  <Coins className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                    {user?.credits !== undefined ? user.credits.toFixed(2) : '—'}
                  </span>
                </Link>

                {/* Dark mode toggle */}
                <button
                  onClick={handleToggleDarkMode}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  title={darkMode ? 'Világos mód' : 'Sötét mód'}
                >
                  {darkMode ? (
                    <Sun className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600" />
                  )}
                </button>

                <div className="relative">
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Értesítések"
                    aria-label={notifications.length > 0 ? `${notifications.length} olvasatlan értesítés` : 'Értesítések'}
                  >
                    <Bell className="w-5 h-5" />
                  </button>
                  {notifications.length > 0 && (
                    <span
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold leading-none text-white bg-red-500 rounded-full ring-2 ring-white dark:ring-gray-800 pointer-events-none z-10"
                      aria-hidden
                    >
                      {notifications.length > 9 ? '9+' : notifications.length}
                    </span>
                  )}
                  
                  {showNotifications && (
                    <>
                      <div
                        className="fixed inset-0 z-40 md:z-10"
                        onClick={() => setShowNotifications(false)}
                      />
                      <div
                        className="fixed inset-x-3 top-[4.25rem] z-50 max-md:max-h-[min(20rem,calc(100vh-5.5rem))]
                          md:absolute md:inset-x-auto md:top-full md:right-0 md:left-auto md:mt-2 md:max-h-80
                          w-auto md:w-96 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                      >
                        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Értesítések</h3>
                          {notifications.length > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {notifications.length} olvasatlan – kattints a megnyitáshoz
                            </p>
                          )}
                        </div>
                        {notifications.length === 0 ? (
                          <div className="px-4 py-8 text-center">
                            <Bell className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                            <p className="text-sm text-gray-500 dark:text-gray-400">Nincs új értesítés</p>
                          </div>
                        ) : (
                          <div className="max-h-80 overflow-y-auto p-2">
                            {notifications.map((notification) => (
                              <button
                                key={notification.id}
                                type="button"
                                onClick={() => openNotification(notification)}
                                className="w-full text-left px-3 py-3 rounded-lg bg-primary-50/90 dark:bg-primary-900/25 hover:bg-primary-100 dark:hover:bg-primary-900/40 border border-primary-100 dark:border-primary-800/60 transition-colors group mb-1 last:mb-0"
                              >
                                <div className="flex items-start gap-3">
                                  <span
                                    className="mt-1.5 w-2.5 h-2.5 rounded-full bg-primary-500 flex-shrink-0 ring-2 ring-primary-200 dark:ring-primary-800"
                                    title="Olvasatlan"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                                      {notification.title}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      {formatNotificationDate(notification.createdAt)}
                                    </p>
                                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400 mt-2 flex items-center gap-0.5">
                                      Megnyitás
                                      <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                                    </p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <Modal
                  isOpen={!!selectedNotification}
                  onClose={closeNotificationModal}
                  closeOnBackdrop={false}
                  title={selectedNotification?.title ?? ''}
                  size="md"
                  footer={
                    <Button onClick={handleMarkAsRead} loading={markingRead}>
                      Olvasottnak jelölöm
                    </Button>
                  }
                >
                  {selectedNotification && (
                    <div className="space-y-3">
                      {selectedNotification.version && (
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400">
                          Verzió: v{selectedNotification.version}
                        </p>
                      )}
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                        {selectedNotification.message}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatNotificationDate(selectedNotification.createdAt)}
                      </p>
                    </div>
                  )}
                </Modal>
                
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary-600" />
                    </div>
                    <span className="hidden md:block text-sm font-medium">{user?.name}</span>
                  </button>
                  
                  {showUserMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowUserMenu(false)}
                      />
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                        <Link
                          to="/profile"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Profil
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          <LogOut className="w-4 h-4" />
                          Kijelentkezés
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <Link to="/login" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                Bejelentkezés
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
